import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { PAT_OVERVIEW_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { PaymentAtTableStoreDispatcher } from '@common/dispatchers/payment-at-table.dispatcher';
import type { PaymentAtTableCheckState } from '@store/payment-at-table/payment-at-table.state';
import type { PatOverviewBlockScenarioContent } from '@front-platform/content-builder';

type PatOverviewBlockScenarioCheck = NonNullable<PatOverviewBlockScenarioContent['check']>;

function toPaymentAtTableCheckState(
  check: PatOverviewBlockScenarioCheck,
): PaymentAtTableCheckState {
  const transactions = check.transactions.map(transaction => ({
    ...transaction,
    items: transaction.items ? [...transaction.items] : undefined,
  }));

  return {
    ...check,
    items: check.items.map(item => ({
      ...item,
      selected_by: { ...item.selected_by },
    })),
    transactions,
    active_transaction: check.active_transaction
      ? {
          ...check.active_transaction,
          items: check.active_transaction.items ? [...check.active_transaction.items] : undefined,
        }
      : undefined,
    shop: {
      ...check.shop,
      current_user_roles: [...check.shop.current_user_roles],
    },
    marketplace: {
      ...check.marketplace,
      current_user_roles: [...check.marketplace.current_user_roles],
    },
    onsite_location: {
      ...check.onsite_location,
    },
    available_payment_methods:
      check.available_payment_methods as PaymentAtTableCheckState['available_payment_methods'],
    error: null,
  };
}

@Injectable()
export class PatOverviewBlockPreviewBootstrapService {
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly paymentAtTableDispatcher = inject(PaymentAtTableStoreDispatcher);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    if (!this.previewState.isActive()) {
      return;
    }

    previewScenarioForBloc$(PAT_OVERVIEW_BLOCK_COMPONENT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(scenario => {
        const vm = scenario
          ? previewScenarioData<PatOverviewBlockScenarioContent>(
              scenario,
              PAT_OVERVIEW_BLOCK_COMPONENT,
            )
          : null;
        if (!vm?.check) {
          this.paymentAtTableDispatcher.resetCheck();
          return;
        }

        this.paymentAtTableDispatcher.updateCheck(toPaymentAtTableCheckState(vm.check));
      });
  }
}
